import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { readMetricsSnapshot, triggerRefresh } from "../lib/metricsCache";
import { buildCountriesList, buildCitiesList, Duration, GeoSection, Section } from "../lib/leaderboard";

const formatNumber = (n) => new Intl.NumberFormat("en-US").format(n);

const PLATFORM_LABELS = {
    facebook: "Facebook",
    x: "X",
    threads: "Threads",
    whatsapp: "WhatsApp",
    linkedin: "LinkedIn",
    instagram: "Instagram",
    tiktok: "TikTok",
};

const PLATFORM_COLORS = {
    facebook: "bg-[#3B5998]",
    x: "bg-brand-ink",
    threads: "bg-black",
    whatsapp: "bg-[#5DCA45]",
    linkedin: "bg-[#2D64BC]",
    instagram: "bg-[#E48242]",
    tiktok: "bg-zinc-700",
    "(direct)": "bg-brand-faint",
};

const platformLabel = (k) => (k === "(direct)" ? "(direct)" : PLATFORM_LABELS[k] || k);
const platformColor = (k) => PLATFORM_COLORS[k] || "bg-brand-faint";

const initial = (name) => (name && name.length > 0 ? name.charAt(0).toUpperCase() : "?");

const BarRow = ({ label, prefix, value, max, accent, sub, dotClass }) => {
    const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
    return (
        <div>
            <div className="flex items-baseline justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                    {dotClass && <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />}
                    {prefix && <span className="text-base flex-shrink-0">{prefix}</span>}
                    <span className="truncate text-brand-ink font-medium">{label}</span>
                </div>
                <div className="flex items-baseline gap-2 flex-shrink-0">
                    <span className="font-brand font-bold text-brand-ink">{formatNumber(value)}</span>
                    {sub && <span className="text-xs text-brand-muted">{sub}</span>}
                </div>
            </div>
            <div className="mt-1.5 h-1.5 bg-brand-panel rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${accent}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const MemberResults = () => {
    const { memberId } = useParams();
    const [metrics, setMetrics] = useState(null);
    const [memberName, setMemberName] = useState(null);
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const [snapshot, memberSnap, mediaSnap] = await Promise.all([
                    readMetricsSnapshot(),
                    getDoc(doc(db, "members", memberId)),
                    getDocs(collection(db, "media")),
                ]);
                if (cancelled) return;
                if (memberSnap.exists()) {
                    const d = memberSnap.data() || {};
                    setMemberName(d.name || null);
                }
                setMedia(mediaSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

                if (snapshot && snapshot.payload) {
                    setMetrics(snapshot.payload);
                    setLoading(false);
                    if (snapshot.isStale) {
                        const fresh = await triggerRefresh();
                        if (!cancelled && fresh) setMetrics(fresh);
                    }
                    return;
                }

                const fresh = await triggerRefresh();
                if (cancelled) return;
                if (fresh) setMetrics(fresh);
                else setError({ error: "metrics_unavailable" });
            } catch (err) {
                if (!cancelled) setError({ error: "fetch_failed", detail: err.message });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [memberId]);

    const asRows = (v) => (Array.isArray(v) ? v : []);

    const comboRows = asRows(metrics && metrics.metrics && metrics.metrics.combos);
    const memberCombos = useMemo(() => comboRows.filter((c) => c.term === memberId), [comboRows, memberId]);
    const totalClicks = useMemo(() => memberCombos.reduce((s, c) => s + (c.y || 0), 0), [memberCombos]);

    const memberGeo = (metrics && metrics.byTerm && metrics.byTerm[memberId]) || null;
    const countriesList = useMemo(
        () => buildCountriesList(memberGeo && memberGeo.country),
        [memberGeo]
    );
    const citiesList = useMemo(
        () => buildCitiesList(memberGeo && memberGeo.city),
        [memberGeo]
    );

    const mediaById = useMemo(() => {
        const map = new Map();
        media.forEach((m) => map.set(m.id, m));
        return map;
    }, [media]);

    const platformList = useMemo(() => {
        const m = new Map();
        memberCombos.forEach((c) => {
            const k = c.medium || "(direct)";
            m.set(k, (m.get(k) || 0) + (c.y || 0));
        });
        return Array.from(m.entries())
            .map(([k, v]) => ({
                key: k,
                label: platformLabel(k),
                value: v,
                sub: totalClicks > 0 ? `${Math.round((v / totalClicks) * 100)}%` : null,
            }))
            .sort((a, b) => b.value - a.value);
    }, [memberCombos, totalClicks]);

    const postList = useMemo(() => {
        const m = new Map();
        memberCombos.forEach((c) => {
            if (!c.content) return;
            m.set(c.content, (m.get(c.content) || 0) + (c.y || 0));
        });
        return Array.from(m.entries())
            .map(([id, value]) => {
                const post = mediaById.get(id);
                return {
                    id,
                    title: post ? post.title : "(unmatched post)",
                    image_url: post ? post.image_url : null,
                    value,
                };
            })
            .sort((a, b) => b.value - a.value);
    }, [memberCombos, mediaById]);

    const maxPlatform = Math.max(1, ...platformList.map((r) => r.value));
    const maxPost = Math.max(1, ...postList.map((r) => r.value));

    const topPlatform = platformList[0];
    const topPost = postList[0];

    const handleShareMyPage = async () => {
        const url = `${window.location.origin}/m/${memberId}`;
        try {
            if (navigator.share) {
                await navigator.share({ url, title: memberName ? `${memberName} on Ambasada` : "My page on Ambasada" });
                return;
            }
        } catch {}
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };

    const displayName = memberName || "Anonymous member";

    return (
        <div className="bg-brand-bg min-h-screen">
            <header className="bg-white border-b border-brand-panel">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12 lg:py-14">
                    <div className="flex items-center justify-between mb-6">
                        <Link to="/results" className="text-sm text-brand-muted hover:text-brand-ink transition-colors inline-flex items-center gap-1.5">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            All results
                        </Link>
                        <button
                            type="button"
                            onClick={handleShareMyPage}
                            className="text-sm font-semibold px-4 py-2 rounded-lg bg-brand-ink text-white hover:bg-black transition-colors"
                        >
                            {copied ? "Link copied" : "Share my page"}
                        </button>
                    </div>
                    <div className="flex items-center gap-5 sm:gap-6">
                        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-brand-red text-white flex items-center justify-center font-brand font-bold text-2xl sm:text-3xl">
                            {initial(displayName)}
                        </div>
                        <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                Community member
                            </div>
                            <h1 className="font-brand text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-[1.05]">
                                {displayName}<span className="text-brand-red">.</span>
                            </h1>
                        </div>
                    </div>
                    <div className="mt-6 h-1 w-24 bg-brand-red rounded-full" />
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
                {loading && <div className="text-brand-muted">Loading...</div>}

                {error && (
                    <div className="rounded-2xl bg-white border border-brand-red/30 p-6">
                        <div className="text-sm uppercase tracking-wider text-brand-red font-semibold">
                            Metrics unavailable
                        </div>
                        <div className="mt-2 text-brand-ink text-sm">
                            {error.detail || error.error || "Unknown error"}
                        </div>
                    </div>
                )}

                {!loading && !error && metrics && (
                    <>
                        <section className="rounded-3xl bg-white border border-brand-panel shadow-brand p-8 sm:p-12 lg:p-14">
                            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-red">
                                {displayName}'s share metrics
                            </div>

                            {(() => {
                                const hasTime = memberGeo && memberGeo.stats && memberGeo.stats.totaltime > 0;
                                return (
                                    <div className="mt-8 flex flex-col sm:flex-row sm:items-stretch gap-6 sm:gap-0">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                {formatNumber(totalClicks)}
                                            </div>
                                            <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                {totalClicks === 1 ? "click" : "clicks"} delivered
                                            </div>
                                        </div>
                                        {hasTime && (
                                            <>
                                                <div className="hidden sm:block w-px self-stretch bg-brand-panel mx-6 lg:mx-10" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                        <Duration seconds={memberGeo.stats.totaltime} />
                                                    </div>
                                                    <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                        of prime time
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}

                            {(topPlatform || topPost) && (
                                <div className="mt-8 pt-8 border-t border-brand-panel grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {topPlatform && (
                                        <div>
                                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-faint">
                                                Best channel
                                            </div>
                                            <div className="mt-1 flex items-center gap-2">
                                                <span className={`inline-block w-2.5 h-2.5 rounded-full ${platformColor(topPlatform.key)}`} />
                                                <span className="font-brand font-bold text-brand-ink text-xl">
                                                    {topPlatform.label}
                                                </span>
                                                <span className="text-sm text-brand-muted">
                                                    {formatNumber(topPlatform.value)} clicks
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {topPost && (
                                        <div>
                                            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-faint">
                                                Best post
                                            </div>
                                            <div className="mt-1 flex items-center gap-2.5">
                                                {topPost.image_url && (
                                                    <img src={topPost.image_url} alt="" className="w-8 h-8 rounded-md object-cover border border-brand-panel flex-shrink-0" />
                                                )}
                                                <span className="font-brand font-bold text-brand-ink text-base truncate">
                                                    {topPost.title}
                                                </span>
                                                <span className="text-sm text-brand-muted whitespace-nowrap">
                                                    {formatNumber(topPost.value)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {totalClicks === 0 && (
                            <Section eyebrow="No clicks yet" title="Nothing has landed yet" lede="Once a share from this member drives a click, the breakdown below fills in." >
                                <div className="rounded-2xl bg-white border border-brand-panel p-6 text-sm text-brand-muted">
                                    Share a post and check back here in a few minutes.
                                </div>
                            </Section>
                        )}

                        {totalClicks > 0 && (
                            <>
                                <Section eyebrow="By post" title="Which posts landed" lede="Posts this member shared, ranked by clicks that actually reached the destination.">
                                    {postList.length === 0 ? (
                                        <div className="rounded-2xl bg-white border border-brand-panel p-6 text-sm text-brand-muted">
                                            No per-post breakdown yet.
                                        </div>
                                    ) : (
                                        <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                                            <div className="space-y-4">
                                                {postList.slice(0, 10).map((row, i) => (
                                                    <div key={row.id} className="flex items-center gap-4">
                                                        <div className="text-brand-faint font-brand font-bold w-6 text-right text-sm">
                                                            {i + 1}
                                                        </div>
                                                        {row.image_url ? (
                                                            <img src={row.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-brand-panel flex-shrink-0" />
                                                        ) : (
                                                            <div className="w-12 h-12 rounded-lg bg-brand-panel flex-shrink-0" />
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-brand-ink truncate">{row.title}</div>
                                                            <div className="mt-1.5 h-1.5 bg-brand-panel rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-brand-red rounded-full"
                                                                    style={{ width: `${Math.max(2, Math.round((row.value / maxPost) * 100))}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <div className="font-brand font-bold text-brand-ink text-lg leading-none">
                                                                {formatNumber(row.value)}
                                                            </div>
                                                            <div className="text-xs text-brand-muted mt-1">
                                                                {totalClicks > 0 ? Math.round((row.value / totalClicks) * 100) : 0}%
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Section>

                                <GeoSection
                                    eyebrow="The places"
                                    title="Where the clicks land"
                                    lede={`Countries and cities for clicks ${displayName} personally drove.`}
                                    countries={countriesList}
                                    cities={citiesList}
                                />

                                <Section eyebrow="The channels" title="Where they shared" lede={`Platforms ${displayName} used.`}>
                                    <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                                        <div className="space-y-3.5">
                                            {platformList.map((row) => (
                                                <BarRow
                                                    key={row.key}
                                                    label={row.label}
                                                    value={row.value}
                                                    max={maxPlatform}
                                                    accent={platformColor(row.key)}
                                                    dotClass={platformColor(row.key)}
                                                    sub={row.sub}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </Section>
                            </>
                        )}

                        <section className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-brand-muted">
                            <Link to="/results" className="font-medium text-brand-ink hover:text-brand-red transition-colors whitespace-nowrap">
                                Back to all results
                            </Link>
                            <Link to="/" className="font-medium text-brand-ink hover:text-brand-red transition-colors whitespace-nowrap">
                                Back to the feed
                            </Link>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default MemberResults;
